import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import { useParams } from 'react-router-dom';
import {
  Container,
  Box,
  Paper,
  Typography,
  TextField,
  FormControlLabel,
  Checkbox,
  Button,
  Breadcrumbs,
  Link,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Home as HomeIcon,
  People as PeopleIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Check as CheckIcon,
  Cancel as CancelIcon,
  ContentCopy as ContentCopyIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import Layout from './Layout';

const NovoUsuario = () => {
  // Obter o id do usuário da URL, se existir
  const { id } = useParams();
  const isEditMode = !!id;
  
  // Estados para o formulário
  const [username, setUsername] = useState('');
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [email, setEmail] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [passwordCopied, setPasswordCopied] = useState(false);
  const [alterarSenha, setAlterarSenha] = useState(false);
  
  // Estados para validação de senha
  const [requisitos, setRequisitos] = useState({
    comprimento: false,
    minuscula: false,
    maiuscula: false,
    numero: false,
    especial: false
  });
  
  // Estados para feedback ao usuário
  const [loading, setLoading] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  // Referência para controlar se é a primeira renderização
  const isFirstRender = useRef(true);
  
  // Carregar dados do usuário se for modo de edição
  useEffect(() => {
    if (isEditMode) {
      const carregarUsuario = async () => {
        try {
          setCarregando(true);
          const token = localStorage.getItem('token');
          
          // Verificar se está autenticado
          if (!token) {
            window.location.href = '/login';
            return;
          }
          
          const response = await api.get(`/usuarios/${id}`);
          
          const userData = response.data;
          
          setUsername(userData.username || '');
          setIsAdmin(userData.is_admin || false);
          setEmail(userData.email || '');
          // Não carregamos a senha, pois ela estará hasheada
          
          if (isEditMode) {
            // Gerar senha aleatória apenas se o usuário decidir alterar a senha
            const novaSenha = gerarSenhaAleatoria();
            setGeneratedPassword(novaSenha);
            validarSenha(novaSenha);
          }
          
        } catch (err) {
          console.error('Erro ao carregar usuário:', err);
          setError(err.response?.data?.message || 'Erro ao carregar dados do usuário.');
        } finally {
          setCarregando(false);
        }
      };
      
      carregarUsuario();
    } else {
      // Se for modo de criação, gerar senha aleatória
      const novaSenha = gerarSenhaAleatoria();
      setGeneratedPassword(novaSenha);
      validarSenha(novaSenha);
    }
  }, [id, isEditMode]);
  
  // Validar requisitos da senha
  const validarSenha = (senha) => {
    const reqs = {
      comprimento: senha.length >= 8,
      minuscula: /[a-z]/.test(senha),
      maiuscula: /[A-Z]/.test(senha),
      numero: /[0-9]/.test(senha),
      especial: /[!@#$%&*]/.test(senha)
    };
    
    setRequisitos(reqs);
    
    // Retornar true apenas se todos os requisitos forem atendidos
    return reqs.comprimento && reqs.minuscula && reqs.maiuscula && reqs.numero && reqs.especial;
  };
  
  // Gerar senha aleatória que atenda aos requisitos
  const gerarSenhaAleatoria = () => {
    // Função para gerar uma senha
    const gerarSenha = () => {
      // Garantir pelo menos um de cada tipo
      const minuscula = String.fromCharCode(97 + Math.floor(Math.random() * 26)); // a-z
      const maiuscula = String.fromCharCode(65 + Math.floor(Math.random() * 26)); // A-Z
      const numero = String.fromCharCode(48 + Math.floor(Math.random() * 10)); // 0-9
      const especiais = '!@#$%&*';
      const especial = especiais.charAt(Math.floor(Math.random() * especiais.length));
      
      // Caracteres para completar a senha (letras, números e especiais)
      const todosCaracteres = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%&*';
      
      // Gerar restante aleatório até completar 8 caracteres
      let restante = '';
      for (let i = 0; i < 4; i++) {
        restante += todosCaracteres.charAt(Math.floor(Math.random() * todosCaracteres.length));
      }
      
      // Juntar todos os caracteres
      const caracteres = [minuscula, maiuscula, numero, especial, ...restante.split('')];
      
      // Embaralhar os caracteres
      for (let i = caracteres.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [caracteres[i], caracteres[j]] = [caracteres[j], caracteres[i]];
      }
      
      return caracteres.join('');
    };
    
    // Gerar senha e verificar se atende a todos os requisitos
    let senha = gerarSenha();
    
    // Verificar se a senha gerada atende a todos os requisitos
    // Se não atender, gerar uma nova senha (embora isso seja improvável devido à nossa lógica)
    const verificarRequisitos = (senha) => {
      return senha.length >= 8 && 
             /[a-z]/.test(senha) && 
             /[A-Z]/.test(senha) && 
             /[0-9]/.test(senha) && 
             /[!@#$%&*]/.test(senha);
    };
    
    // Tentar até 3 vezes para garantir uma senha válida
    let tentativas = 0;
    while (!verificarRequisitos(senha) && tentativas < 3) {
      senha = gerarSenha();
      tentativas++;
    }
    
    return senha;
  };
  
  // Função para alternar visibilidade da senha
  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };
  
  // Função para copiar a senha para a área de transferência
  const handleCopyPassword = () => {
    navigator.clipboard.writeText(generatedPassword);
    setPasswordCopied(true);
    setTimeout(() => setPasswordCopied(false), 2000);
  };
  
  // Função para gerar uma nova senha
  const handleRegeneratePassword = () => {
    const novaSenha = gerarSenhaAleatoria();
    setGeneratedPassword(novaSenha);
    validarSenha(novaSenha);
  };
  
  // Garantir que a senha gerada é válida
  useEffect(() => {
    // Pular a primeira renderização para evitar loop infinito
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    
    // Verificar se há uma senha e se não atende a todos os requisitos
    const senhaInvalida = generatedPassword && 
                         (!requisitos.comprimento || 
                          !requisitos.minuscula || 
                          !requisitos.maiuscula || 
                          !requisitos.numero || 
                          !requisitos.especial);
    
    if (senhaInvalida) {
      console.log("Senha não atende aos requisitos, gerando nova senha");
      const novaSenha = gerarSenhaAleatoria();
      setGeneratedPassword(novaSenha);
      validarSenha(novaSenha);
    }
  }, [generatedPassword, requisitos.comprimento, requisitos.minuscula, requisitos.maiuscula, requisitos.numero, requisitos.especial]);
  
  // Verificar se todos os requisitos da senha estão atendidos
  const todosRequisitosAtendidos = () => {
    if (isEditMode && !alterarSenha) {
      // No modo de edição sem alterar senha, não verificamos requisitos
      return true;
    }
    
    return Object.values(requisitos).every(req => req === true);
  };
  
  // Enviar formulário
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (loading || success) return;
    
    // Validações básicas
    if (!username.trim()) {
      setError('Por favor, informe um nome de usuário.');
      return;
    }
    
    // Verificar se a senha atende aos requisitos
    const senhaValida = !isEditMode ? validarSenha(generatedPassword) : true;
    
    if (!isEditMode && !senhaValida) {
      setError('A senha gerada não atende aos requisitos mínimos. Por favor, gere uma nova.');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const dados = {
        username: username.trim(),
        email: email.trim() || null,
        is_admin: isAdmin
      };
      
      if (!isEditMode || alterarSenha) {
        dados.password = generatedPassword;
        dados.primeiro_acesso = true;
      }
      
      if (isEditMode) {
        await api.put(`/usuarios/${id}`, dados);
      } else {
        await api.post('/usuarios', dados);
      }
       
       setSuccess(true);
      
      // Redirecionar após 2 segundos
      setTimeout(() => {
        window.location.href = '/admin/usuarios';
      }, 2000);
      
    } catch (erro) {
      console.error('Erro ao enviar formulário:', erro);
      
      // Tratamento específico para erros do axios
      if (erro.response?.data?.requisitos) {
        setRequisitos(erro.response.data.requisitos);
        setError('A senha não atende aos requisitos de segurança. Por favor, gere uma nova.');
      } else {
        const message = erro.response?.data?.message || erro.message || 'Ocorreu um erro ao processar sua solicitação. Por favor, tente novamente.';
        setError(message);
      }
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Layout>
      <Container maxWidth="sm">
        {/* Cabeçalho e navegação */}
        <Box sx={{ mb: 4 }}>
          <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
            <Link
              underline="hover"
              color="inherit"
              href="/dashboard"
              sx={{ display: 'flex', alignItems: 'center' }}
            >
              <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
              Início
            </Link>
            <Link
              underline="hover"
              color="inherit"
              href="/admin/usuarios"
              sx={{ display: 'flex', alignItems: 'center' }}
            >
              <PeopleIcon sx={{ mr: 0.5 }} fontSize="inherit" />
              Usuários
            </Link>
            <Typography color="text.primary">
              {isEditMode ? 'Editar Usuário' : 'Novo Usuário'}
            </Typography>
          </Breadcrumbs>
          
          <Typography variant="h4" component="h1" gutterBottom>
            {isEditMode ? 'Editar Usuário' : 'Novo Usuário'}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {isEditMode 
              ? 'Atualize os dados do usuário nos campos abaixo' 
              : 'Preencha os dados abaixo para criar um novo usuário'}
          </Typography>
        </Box>
        
        {/* Feedback de erro ou sucesso */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        {carregando ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
            <CircularProgress />
          </Box>
        ) : success ? (
          <Alert severity="success" sx={{ mb: 3 }}>
            <Typography variant="body1" gutterBottom>
              Usuário <strong>{username}</strong> {isEditMode ? 'atualizado' : 'criado'} com sucesso!
            </Typography>
            {(!isEditMode || alterarSenha) && (
              <Typography variant="body2">
                Lembre-se de fornecer a senha temporária ao usuário. Redirecionando...
              </Typography>
            )}
          </Alert>
        ) : (
          /* Formulário */
          <Paper elevation={0} variant="outlined" sx={{ p: 3, mb: 4 }}>
            <form onSubmit={handleSubmit}>
              <TextField
                label="Nome de Usuário"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                fullWidth
                margin="normal"
                variant="outlined"
                autoFocus
                disabled={loading || success}
              />

              <TextField
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                fullWidth
                margin="normal"
                variant="outlined"
                disabled={loading || success}
              />
              
              {isEditMode && (
                <FormControlLabel
                  control={
                    <Checkbox 
                      checked={alterarSenha} 
                      onChange={(e) => setAlterarSenha(e.target.checked)}
                      disabled={loading || success}
                    />
                  }
                  label="Resetar senha do usuário"
                  sx={{ mt: 2, mb: 1 }}
                />
              )}
              
              {(!isEditMode || alterarSenha) && (
                <>
                  <Box sx={{ mt: 3, mb: 1 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Senha temporária (gerada automaticamente):
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Esta senha será válida apenas para o primeiro acesso.
                      O usuário será obrigado a alterá-la ao fazer login.
                    </Typography>
                  </Box>
                  
                  <FormControl fullWidth margin="normal" variant="outlined">
                    <InputLabel htmlFor="password">Senha temporária</InputLabel>
                    <OutlinedInput
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={generatedPassword}
                      readOnly
                      endAdornment={
                        <InputAdornment position="end">
                          <Tooltip title={passwordCopied ? "Copiado!" : "Copiar senha"}>
                            <IconButton
                              onClick={handleCopyPassword}
                              edge="end"
                              sx={{ mr: 0.5 }}
                              color={passwordCopied ? "success" : "default"}
                            >
                              <ContentCopyIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Gerar nova senha">
                            <IconButton
                              onClick={handleRegeneratePassword}
                              edge="end"
                              sx={{ mr: 0.5 }}
                            >
                              <RefreshIcon />
                            </IconButton>
                          </Tooltip>
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={handleTogglePasswordVisibility}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      }
                      label="Senha temporária"
                    />
                  </FormControl>
                  
                  {/* Requisitos de senha */}
                  <Box sx={{ mt: 2, mb: 3 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Requisitos de senha:
                    </Typography>
                    
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {requisitos.comprimento ? (
                          <CheckIcon color="success" fontSize="small" sx={{ mr: 1 }} />
                        ) : (
                          <CancelIcon color="error" fontSize="small" sx={{ mr: 1 }} />
                        )}
                        <Typography 
                          variant="body2" 
                          color={requisitos.comprimento ? 'success.main' : 'error.main'}
                        >
                          Mínimo 8 caracteres
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {requisitos.minuscula ? (
                          <CheckIcon color="success" fontSize="small" sx={{ mr: 1 }} />
                        ) : (
                          <CancelIcon color="error" fontSize="small" sx={{ mr: 1 }} />
                        )}
                        <Typography 
                          variant="body2" 
                          color={requisitos.minuscula ? 'success.main' : 'error.main'}
                        >
                          Letra minúscula
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {requisitos.maiuscula ? (
                          <CheckIcon color="success" fontSize="small" sx={{ mr: 1 }} />
                        ) : (
                          <CancelIcon color="error" fontSize="small" sx={{ mr: 1 }} />
                        )}
                        <Typography 
                          variant="body2" 
                          color={requisitos.maiuscula ? 'success.main' : 'error.main'}
                        >
                          Letra maiúscula
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {requisitos.numero ? (
                          <CheckIcon color="success" fontSize="small" sx={{ mr: 1 }} />
                        ) : (
                          <CancelIcon color="error" fontSize="small" sx={{ mr: 1 }} />
                        )}
                        <Typography 
                          variant="body2" 
                          color={requisitos.numero ? 'success.main' : 'error.main'}
                        >
                          Número
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {requisitos.especial ? (
                          <CheckIcon color="success" fontSize="small" sx={{ mr: 1 }} />
                        ) : (
                          <CancelIcon color="error" fontSize="small" sx={{ mr: 1 }} />
                        )}
                        <Typography 
                          variant="body2" 
                          color={requisitos.especial ? 'success.main' : 'error.main'}
                        >
                          Caractere especial (!@#$%&*)
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </>
              )}
              
              {/* Opção de administrador */}
              <FormControlLabel
                control={
                  <Checkbox 
                    checked={isAdmin} 
                    onChange={(e) => setIsAdmin(e.target.checked)}
                    disabled={loading || success}
                  />
                }
                label="Usuário Administrador"
                sx={{ mt: 1, mb: 3 }}
              />
              
              {/* Botões de ação */}
              <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                <Button
                  variant="outlined"
                  onClick={() => window.location.href = '/admin/usuarios'}
                  disabled={loading}
                  sx={{ flexGrow: 1 }}
                >
                  Cancelar
                </Button>
                
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={loading || success || !username.trim() || !todosRequisitosAtendidos()}
                  sx={{ flexGrow: 1 }}
                >
                  {loading ? (
                    <CircularProgress size={24} />
                  ) : isEditMode ? (
                    'Salvar Alterações'
                  ) : (
                    'Criar Usuário'
                  )}
                </Button>
              </Box>
            </form>
          </Paper>
        )}
      </Container>
    </Layout>
  );
};

export default NovoUsuario; 